/**
 * Heuristic trick-play for Armenian Belot bots (local lobby).
 *
 * Modes:
 * - `simple`: smallest card id among legal (legacy behaviour).
 * - `pro`: smearing (partner winning), trashing (cannot take trick), trump pull on lead.
 *
 * @module game/belotArmenian/botPlay
 */

const { trickWinner, cardPointsTowardRoundTotal } = require("./tricks");
const { cardSuit, cardRank, teamOfSeat, trickStrengthForRank } = require("./types");

/** Минимум козырей на руке, чтобы на лиде тянуть козырь (сценарий 3). */
const MIN_TRUMP_COUNT_FOR_LEAD_PULL = 4;

/**
 * @param {number[]} legalCards
 * @returns {number|null}
 */
function pickSimpleCard(legalCards) {
  if (!Array.isArray(legalCards) || legalCards.length === 0) return null;
  if (legalCards.length === 1) return legalCards[0];
  return legalCards.reduce((a, b) => (a < b ? a : b));
}

/**
 * @param {number[]} legalCards
 * @param {number|null} trumpSuit
 */
function compareMinPointsThenId(a, b, trumpSuit) {
  const pa = cardPointsTowardRoundTotal(a, trumpSuit);
  const pb = cardPointsTowardRoundTotal(b, trumpSuit);
  if (pa !== pb) return pa - pb;
  const junkA = isLowNonTrumpJunk(a, trumpSuit) ? 0 : 1;
  const junkB = isLowNonTrumpJunk(b, trumpSuit) ? 0 : 1;
  if (junkA !== junkB) return junkA - junkB;
  return a - b;
}

/**
 * @param {number} card
 * @param {number|null} trumpSuit
 */
function isLowNonTrumpJunk(card, trumpSuit) {
  const r = cardRank(card);
  const isTrump = trumpSuit != null && cardSuit(card) === trumpSuit;
  if (isTrump) return false;
  return r === 0 || r === 1 || r === 2; // 7, 8, 9
}

/**
 * @param {number[]} legalCards
 * @param {number|null} trumpSuit
 * @returns {number}
 */
function pickMinPointsCard(legalCards, trumpSuit) {
  return legalCards.reduce((a, b) => (compareMinPointsThenId(a, b, trumpSuit) <= 0 ? a : b));
}

/**
 * @param {number[]} legalCards
 * @param {number|null} trumpSuit
 * @returns {number}
 */
function pickMaxPointsCard(legalCards, trumpSuit) {
  return legalCards.reduce((a, b) => {
    const pa = cardPointsTowardRoundTotal(a, trumpSuit);
    const pb = cardPointsTowardRoundTotal(b, trumpSuit);
    if (pa !== pb) return pa > pb ? a : b;
    const ra = cardRank(a);
    const rb = cardRank(b);
    if (ra !== rb) return ra > rb ? a : b;
    return a < b ? a : b;
  });
}

/**
 * @param {number[]} hand
 * @param {number|null} trumpSuit
 */
function countTrumpsInHand(hand, trumpSuit) {
  if (!Array.isArray(hand) || trumpSuit == null) return 0;
  let n = 0;
  for (const c of hand) {
    if (cardSuit(c) === trumpSuit) n += 1;
  }
  return n;
}

/**
 * Лид с козырём: среди легальных козырей не вести с единственного максимума по силе взятки, если есть слабее.
 *
 * @param {number[]} legalTrumpCards
 * @param {number|null} trumpSuit
 * @returns {number}
 */
function pickLeadTrumpCard(legalTrumpCards, trumpSuit) {
  if (legalTrumpCards.length === 1) return legalTrumpCards[0];
  const scored = legalTrumpCards.map((c) => ({
    c,
    str: trickStrengthForRank(cardRank(c), true),
    pts: cardPointsTowardRoundTotal(c, trumpSuit),
  }));
  const maxStr = Math.max(...scored.map((x) => x.str));
  const notMax = scored.filter((x) => x.str < maxStr).map((x) => x.c);
  const pool = notMax.length ? notMax : legalTrumpCards;
  return pool.reduce((a, b) => {
    const pa = cardPointsTowardRoundTotal(a, trumpSuit);
    const pb = cardPointsTowardRoundTotal(b, trumpSuit);
    if (pa !== pb) return pa < pb ? a : b;
    const sa = trickStrengthForRank(cardRank(a), true);
    const sb = trickStrengthForRank(cardRank(b), true);
    if (sa !== sb) return sa < sb ? a : b;
    return a < b ? a : b;
  });
}

/**
 * @param {{ seat: number, card: number }[]} trickSoFar
 * @param {number|null} trumpSuit
 */
function winnerAmongPlays(trickSoFar, trumpSuit) {
  if (!Array.isArray(trickSoFar) || trickSoFar.length === 0) return null;
  return trickWinner(trickSoFar, trumpSuit);
}

/**
 * @param {{ seat: number, card: number }[]} trickSoFar
 * @param {number} seat
 * @param {number[]} legalCards
 * @param {number|null} trumpSuit
 * @returns {boolean}
 */
function canTeamWinTrickLastSeat(trickSoFar, seat, legalCards, trumpSuit) {
  const myTeam = teamOfSeat(seat);
  for (const c of legalCards) {
    const w = trickWinner([...trickSoFar, { seat, card: c }], trumpSuit);
    if (teamOfSeat(w) === myTeam) return true;
  }
  return false;
}

/**
 * Среди карт, после которых команда бота забирает взятку (последний ход), выбрать победную с минимальными очками.
 *
 * @param {{ seat: number, card: number }[]} trickSoFar
 * @param {number} seat
 * @param {number[]} legalCards
 * @param {number|null} trumpSuit
 * @returns {number|null}
 */
function pickCheapestWinningCardLastSeat(trickSoFar, seat, legalCards, trumpSuit) {
  const myTeam = teamOfSeat(seat);
  /** @type {number[]} */
  const winners = [];
  for (const c of legalCards) {
    const w = trickWinner([...trickSoFar, { seat, card: c }], trumpSuit);
    if (teamOfSeat(w) === myTeam) winners.push(c);
  }
  if (winners.length === 0) return null;
  return winners.reduce((a, b) => {
    const pa = cardPointsTowardRoundTotal(a, trumpSuit);
    const pb = cardPointsTowardRoundTotal(b, trumpSuit);
    if (pa !== pb) return pa < pb ? a : b;
    const sa = trickStrengthForRank(cardRank(a), trumpSuit != null && cardSuit(a) === trumpSuit);
    const sb = trickStrengthForRank(cardRank(b), trumpSuit != null && cardSuit(b) === trumpSuit);
    if (sa !== sb) return sa < sb ? a : b;
    return a < b ? a : b;
  });
}

/**
 * Без козыря на лиде: слабая карта с минимальными очками.
 *
 * @param {number[]} legalCards
 * @param {number|null} trumpSuit
 */
function pickNoTrumpLead(legalCards, trumpSuit) {
  return pickMinPointsCard(legalCards, trumpSuit);
}

/**
 * Эвристика «профи».
 *
 * @param {any} state
 * @param {number} seat
 * @param {number[]} legalCards
 * @returns {number|null}
 */
function pickProCard(state, seat, legalCards) {
  if (!Array.isArray(legalCards) || legalCards.length === 0) return null;
  if (legalCards.length === 1) return legalCards[0];

  const c = state.contract;
  const trumpSuit = c && typeof c === "object" ? c.trumpSuit : null;
  const trickSoFar = Array.isArray(state.currentTrick) ? state.currentTrick : [];
  const hand = Array.isArray(state.hands?.[seat]) ? state.hands[seat] : [];

  // --- Лид: козырь / без козыря ---
  if (trickSoFar.length === 0) {
    if (trumpSuit != null && countTrumpsInHand(hand, trumpSuit) >= MIN_TRUMP_COUNT_FOR_LEAD_PULL) {
      const trumpLegals = legalCards.filter((x) => cardSuit(x) === trumpSuit);
      if (trumpLegals.length > 0) return pickLeadTrumpCard(trumpLegals, trumpSuit);
    }
    if (trumpSuit == null) return pickNoTrumpLead(legalCards, trumpSuit);
    return pickMinPointsCard(legalCards, trumpSuit);
  }

  const winSeat = winnerAmongPlays(trickSoFar, trumpSuit);
  if (winSeat == null) return pickSimpleCard(legalCards);
  const myTeam = teamOfSeat(seat);
  const leaderTeam = teamOfSeat(winSeat);

  // --- Поддержка напарника: не сливать очки на уже выигранной взятке ---
  if (leaderTeam === myTeam) {
    /** @type {number[]} */
    const keepPartnerWinning = [];
    for (const c of legalCards) {
      const w = trickWinner([...trickSoFar, { seat, card: c }], trumpSuit);
      if (teamOfSeat(w) === myTeam) keepPartnerWinning.push(c);
    }
    if (keepPartnerWinning.length > 0) {
      return pickMinPointsCard(keepPartnerWinning, trumpSuit);
    }
    return pickMaxPointsCard(legalCards, trumpSuit);
  }

  // --- Соперник лидирует ---
  const isLastInTrick = trickSoFar.length === 3;
  if (isLastInTrick) {
    if (!canTeamWinTrickLastSeat(trickSoFar, seat, legalCards, trumpSuit)) {
      return pickMinPointsCard(legalCards, trumpSuit);
    }
    const cheap = pickCheapestWinningCardLastSeat(trickSoFar, seat, legalCards, trumpSuit);
    return cheap != null ? cheap : pickMinPointsCard(legalCards, trumpSuit);
  }

  // Приближённо: соперник впереди, не последний ход — сброс по минимуму очков
  return pickMinPointsCard(legalCards, trumpSuit);
}

/**
 * @param {'simple'|'pro'} mode
 */
function normalizeMode(mode) {
  return mode === "pro" ? "pro" : "simple";
}

/**
 * @param {any} state
 * @param {number} seat
 * @param {number[]} legalCards
 * @param {'simple'|'pro'} [mode]
 * @returns {number|null}
 */
function pickBotPlayCard(state, seat, legalCards, mode = "simple") {
  const m = normalizeMode(mode);
  if (m === "simple") return pickSimpleCard(legalCards);
  return pickProCard(state, seat, legalCards);
}

/**
 * @param {any} state
 * @param {number} seat
 * @param {number[]} legalCards
 * @param {'simple'|'pro'} [mode]
 * @returns {{ type: 'PLAY_CARD', seat: number, payload: { card: number } } | null}
 */
function pickBotPlayCmd(state, seat, legalCards, mode = "simple") {
  const card = pickBotPlayCard(state, seat, legalCards, mode);
  if (card == null || !Number.isInteger(card)) return null;
  return { type: "PLAY_CARD", seat, payload: { card } };
}

module.exports = {
  MIN_TRUMP_COUNT_FOR_LEAD_PULL,
  pickBotPlayCard,
  pickBotPlayCmd,
  pickSimpleCard,
};
